// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Vector3 from '../../math/Vector3';

/**
 * Container for particle data in the ParticleSystemComponent.
 */
class ParticleData {
 /**
  * @param {ParticleSystemComponent} particleComponent
  */
 constructor(particleComponent) {
   /**
    * The owner component
    * @type {number}
    */
   this.component = particleComponent;

   /**
    * @type {number}
    */
   this.lifeTime = 1;

   /**
    * @type {number}
    */
   this.emitTime = 0;

   /**
    * @type {number}
    */
   this.active = 1;

   /**
    * @type {Vector3}
    */
   this.startPosition = new Vector3();

   /**
    * @type {Vector3}
    */
   this.startDirection = new Vector3();

   /**
    * @type {number}
    */
   this.startAngle = 0;

   /**
    * @type {number}
    */
   this.startSize = 1;

   /**
    * @type {number}
    */
   this.sortValue = 0;

   /**
    * @type {number}
    */
   this.emitRandom = 0;

   /**
    * @type {number}
    */
   this.loopAfter = 0;
 }

 /**
  * Get the world position of the particle
  * @param {Vector3} store
  */
 getWorldPosition(store) {
   if (!this.active) {
     return store;
   }

   const component = this.component;

   // pos + dir * t + 0.5 * t * t * g
   let age = component.time - this.emitTime;

   if (component.loop) {
     age %= this.loopAfter;
   }

   dirDelta.copy(this.startDirection).scale(age);
   gravityDelta.copy(component.localSpace ? component._localGravity : component.gravity).scale(age * age * 0.5);
   store.copy(this.startPosition).add(dirDelta).add(gravityDelta);

   // Add velocity over lifetime
   if (component.localVelocityOverLifetime) {
     var unitAge = age / this.loopAfter;
     component.localVelocityOverLifetime.getVec3IntegralValueAt(unitAge, this.emitRandom, localVelocityDelta);
     localVelocityDelta.applyPost(component._localToWorldRotation);
     store.add(localVelocityDelta);
   }

   if (component.worldVelocityOverLifetime) {
     var unitAge = age / this.loopAfter;
     component.worldVelocityOverLifetime.getVec3IntegralValueAt(unitAge, this.emitRandom, worldVelocityDelta);
     worldVelocityDelta.applyPost(component._worldToLocalRotation);
     store.add(worldVelocityDelta);
   }

   if (component.localSpace) {
     this.component._particleWorldTransform.applyForward(store, store);
   }

   return store;
 }
}

const dirDelta = new Vector3();
const gravityDelta = new Vector3();
const localVelocityDelta = new Vector3();
const worldVelocityDelta = new Vector3();

export default ParticleData;
